/* ==========================================================================
   DEATHBARREL — theme.js
   Progressive enhancement only. Every feature here has a working no-JS
   fallback: menus are links, forms POST to Shopify, quantities submit.
   No framework, no dependencies, ~9KB unminified.
   ========================================================================== */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* --------------------------------------------------------- helpers --- */
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function money(cents) {
    var fmt = (window.DBTheme && window.DBTheme.moneyFormat) || '{{amount}}';
    var amount = (cents / 100).toFixed(2);
    // Shopify money formats we support without a full parser.
    if (fmt.indexOf('amount_no_decimals') > -1) amount = Math.round(cents / 100).toString();
    if (fmt.indexOf('amount_with_comma_separator') > -1) amount = amount.replace('.', ',');
    return fmt.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function announce(message) {
    var region = $('#db-live-region');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(function () { region.textContent = message; }, 60);
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  /* Minimal focus trap shared by the menu and the cart drawer. */
  function trapFocus(container, opener) {
    var previous = opener || document.activeElement;

    function onKeydown(event) {
      if (event.key !== 'Tab') return;
      var items = $$(FOCUSABLE, container).filter(function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    container.addEventListener('keydown', onKeydown);

    // The panel may still be mid-transition; focusing a not-yet-visible element
    // silently fails, so wait for the next frame.
    window.requestAnimationFrame(function () {
      var firstFocusable = $$(FOCUSABLE, container)[0];
      if (firstFocusable) firstFocusable.focus();
    });

    return function release() {
      container.removeEventListener('keydown', onKeydown);
      if (previous && typeof previous.focus === 'function') previous.focus();
    };
  }

  function lockScroll(locked) {
    document.documentElement.style.overflow = locked ? 'hidden' : '';
  }

  /* ------------------------------------------------------ mobile menu --- */
  function initMenu() {
    var menu = $('[data-db-menu]');
    var toggle = $('[data-db-menu-open]');
    if (!menu || !toggle) return;

    var closeBtn = $('[data-db-menu-close]', menu);
    var release = null;

    function open() {
      menu.dataset.open = 'true';
      toggle.setAttribute('aria-expanded', 'true');
      lockScroll(true);
      release = trapFocus(menu, toggle);
    }

    function close() {
      menu.dataset.open = 'false';
      toggle.setAttribute('aria-expanded', 'false');
      lockScroll(false);
      if (release) { release(); release = null; }
    }

    toggle.addEventListener('click', function (e) {
      e.preventDefault();
      menu.dataset.open === 'true' ? close() : open();
    });

    if (closeBtn) closeBtn.addEventListener('click', close);

    // Document level: Escape must close the menu even if focus never made it
    // inside (a blocked focus call must not trap the visitor).
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.dataset.open === 'true') close();
    });

    // Any navigation closes the menu.
    $$('a', menu).forEach(function (link) { link.addEventListener('click', close); });
  }

  /* ------------------------------------------------------ cart drawer --- */
  var CartDrawer = {
    el: null,
    release: null,
    enabled: false,

    init: function () {
      this.el = $('[data-db-drawer]');
      if (!this.el) return;
      this.enabled = true;

      var self = this;

      $$('[data-db-drawer-close]', this.el).forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.el && self.el.dataset.open === 'true') self.close();
      });

      // The header cart link opens the drawer; without JS it navigates to /cart.
      $$('[data-db-cart-open]').forEach(function (link) {
        link.addEventListener('click', function (e) {
          e.preventDefault();
          self.open();
        });
      });

      this.bindLines();
    },

    open: function () {
      if (!this.el) return;
      this.el.dataset.open = 'true';
      lockScroll(true);
      this.release = trapFocus(this.el);
    },

    close: function () {
      if (!this.el) return;
      this.el.dataset.open = 'false';
      lockScroll(false);
      if (this.release) { this.release(); this.release = null; }
    },

    /* Re-fetch the rendered drawer + header count from Shopify's Section
       Rendering API so markup stays in Liquid, not in JavaScript. */
    refresh: function (thenOpen) {
      var self = this;
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + '?sections=cart-drawer,header' : '/?sections=cart-drawer,header')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          if (sections['cart-drawer']) {
            var parsed = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
            var fresh = $('[data-db-drawer]', parsed);
            var current = $('[data-db-drawer]');
            if (fresh && current) {
              var wasOpen = current.dataset.open === 'true';
              current.innerHTML = fresh.innerHTML;
              current.dataset.open = wasOpen ? 'true' : 'false';
              self.el = current;
              self.bindLines();
              $$('[data-db-drawer-close]', current).forEach(function (btn) {
                btn.addEventListener('click', function () { self.close(); });
              });
            }
          }
          if (sections.header) {
            var hdoc = new DOMParser().parseFromString(sections.header, 'text/html');
            $$('[data-db-cart-count]').forEach(function (node) {
              var fresh2 = $('[data-db-cart-count]', hdoc);
              if (fresh2) {
                node.textContent = fresh2.textContent;
                node.dataset.empty = fresh2.dataset.empty;
                var label = fresh2.getAttribute('data-db-cart-label');
                if (label) node.setAttribute('data-db-cart-label', label);
              }
            });
          }
          if (thenOpen) self.open();
        })
        .catch(function () {
          // Network failure: fall back to a full page load of the cart.
          window.location.href = '/cart';
        });
    },

    bindLines: function () {
      var self = this;
      $$('[data-db-line-change]').forEach(function (control) {
        control.addEventListener('click', function (e) {
          if (control.tagName === 'A') e.preventDefault();
          self.change(control.dataset.dbLineKey, control.dataset.dbLineChange);
        });
      });

      $$('[data-db-line-qty]').forEach(function (input) {
        input.addEventListener('change', function () {
          self.change(input.dataset.dbLineKey, input.value);
        });
      });
    },

    change: function (key, quantity) {
      var self = this;
      if (!key) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: key, quantity: Number(quantity) })
      })
        .then(function (r) { return r.json(); })
        .then(function () {
          announce(window.DBTheme && window.DBTheme.strings ? window.DBTheme.strings.cartUpdated : 'Cart updated');
          return self.refresh(false);
        })
        .catch(function () { window.location.href = '/cart'; });
    }
  };

  /* ------------------------------------------------- add-to-cart form --- */
  function initProductForms() {
    /* The variant id field ships disabled so that, with scripts off, the
       <noscript> variant <select> is the only `id` submitted. Now that JS
       is running, enable it and hand variant switching to the picker. */
    $$('[data-db-variant-id][disabled]').forEach(function (input) {
      input.removeAttribute('disabled');
    });

    $$('[data-db-product-form]').forEach(function (form) {
      form.addEventListener('submit', function (e) {
        if (!CartDrawer.enabled) return;   // no drawer -> normal POST to /cart/add
        e.preventDefault();

        var button = $('[data-db-add]', form);
        var errorBox = $('[data-db-form-error]', form);
        var original = button ? button.textContent : '';

        if (errorBox) { errorBox.hidden = true; errorBox.textContent = ''; }
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = (window.DBTheme && window.DBTheme.strings.adding) || 'Adding…';
        }

        var body = new FormData(form);
        body.append('sections', 'cart-drawer,header');

        fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: body })
          .then(function (response) {
            return response.json().then(function (data) {
              if (!response.ok) throw new Error(data.description || data.message || 'Add to cart failed');
              return data;
            });
          })
          .then(function () {
            announce((window.DBTheme && window.DBTheme.strings.added) || 'Added to cart');
            return CartDrawer.refresh(true);
          })
          .catch(function (err) {
            if (errorBox) {
              errorBox.hidden = false;
              errorBox.textContent = err.message;
            }
            announce(err.message);
          })
          .finally(function () {
            if (button) {
              button.removeAttribute('aria-disabled');
              button.textContent = original;
            }
          });
      });
    });
  }

  /* --------------------------------------------------- variant picker --- */
  function initVariantPickers() {
    $$('[data-db-variants]').forEach(function (root) {
      var dataNode = $('[data-db-variant-json]', root);
      if (!dataNode) return;

      var variants;
      try { variants = JSON.parse(dataNode.textContent); } catch (e) { return; }

      var form = root.closest('form') || $('[data-db-product-form]');
      var idInput = form ? $('[data-db-variant-id]', form) : null;
      var priceNode = $('[data-db-price]', root.closest('[data-db-product]') || document);
      var addButton = form ? $('[data-db-add]', form) : null;
      var stockNode = $('[data-db-stock]', root.closest('[data-db-product]') || document);
      var skuNode = $('[data-db-sku]', root.closest('[data-db-product]') || document);

      function selectedOptions() {
        return $$('input[type="radio"]:checked, select', root).map(function (input) {
          return input.value;
        });
      }

      function findVariant(options) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < options.length; j++) {
            if (v.options[j] !== options[j]) { match = false; break; }
          }
          if (match) return v;
        }
        return null;
      }

      /* Mark option values that produce no purchasable variant. Uses a
         line-through + dashed border, never colour alone. */
      function markAvailability(options) {
        $$('fieldset[data-db-option-index]', root).forEach(function (fieldset) {
          var index = Number(fieldset.dataset.dbOptionIndex);
          $$('input[type="radio"]', fieldset).forEach(function (input) {
            var probe = options.slice();
            probe[index] = input.value;
            var candidate = variants.find(function (v) {
              return probe.every(function (val, i) { return i === index ? v.options[i] === val : v.options[i] === val; });
            });
            var label = fieldset.querySelector('label[for="' + input.id + '"]');
            var unavailable = !candidate || !candidate.available;
            if (label) label.setAttribute('data-unavailable', unavailable ? 'true' : 'false');
          });
        });
      }

      function update() {
        var options = selectedOptions();
        var variant = findVariant(options);
        markAvailability(options);

        $$('[data-db-option-selected]', root).forEach(function (node) {
          var index = Number(node.dataset.dbOptionSelected);
          node.textContent = options[index] || '';
        });

        if (!variant) {
          if (addButton) {
            addButton.setAttribute('aria-disabled', 'true');
            addButton.textContent = (window.DBTheme && window.DBTheme.strings.unavailable) || 'Unavailable';
          }
          if (stockNode) stockNode.textContent = (window.DBTheme && window.DBTheme.strings.unavailable) || 'Unavailable';
          return;
        }

        if (idInput) idInput.value = variant.id;

        if (priceNode) {
          priceNode.innerHTML = variant.compare_at_price && variant.compare_at_price > variant.price
            ? '<span class="db-price db-price--sale">' + money(variant.price) + '</span> <s class="db-price__was">' + money(variant.compare_at_price) + '</s>'
            : '<span class="db-price">' + money(variant.price) + '</span>';
        }

        if (skuNode) skuNode.textContent = variant.sku || '';

        if (addButton) {
          if (variant.available) {
            addButton.removeAttribute('aria-disabled');
            addButton.textContent = (window.DBTheme && window.DBTheme.strings.addToCart) || 'Add to cart';
          } else {
            addButton.setAttribute('aria-disabled', 'true');
            addButton.textContent = (window.DBTheme && window.DBTheme.strings.soldOut) || 'Sold out';
          }
        }

        if (stockNode) {
          var strings = (window.DBTheme && window.DBTheme.strings) || {};
          if (!variant.available) {
            stockNode.textContent = strings.soldOut || 'Sold out';
            stockNode.dataset.state = 'sold';
          } else if (variant.inventory_management && variant.inventory_quantity > 0 && variant.inventory_quantity <= (window.DBTheme.lowStockThreshold || 0)) {
            stockNode.textContent = (strings.lowStock || 'Only {{ count }} left').replace('{{ count }}', variant.inventory_quantity);
            stockNode.dataset.state = 'low';
          } else {
            stockNode.textContent = strings.inStock || 'In stock';
            stockNode.dataset.state = 'in';
          }
        }

        // Keep the URL shareable without adding a history entry per click.
        if (variant.id && window.history && window.history.replaceState) {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState({}, '', url.toString());
        }
      }

      root.addEventListener('change', update);
      update();
    });
  }

  /* -------------------------------------------------- quantity input --- */
  function initQuantity() {
    $$('[data-db-qty]').forEach(function (widget) {
      var input = $('input', widget);
      if (!input) return;
      $$('button', widget).forEach(function (button) {
        button.addEventListener('click', function () {
          var step = button.dataset.dbQtyStep === 'down' ? -1 : 1;
          var min = Number(input.min || 1);
          var next = Math.max(min, (Number(input.value) || min) + step);
          if (input.max && next > Number(input.max)) next = Number(input.max);
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });
  }

  /* ------------------------------------------------------- gallery ----- */
  function initGallery() {
    $$('[data-db-gallery]').forEach(function (gallery) {
      var main = $('.db-gallery__main img', gallery);
      if (!main) return;
      $$('[data-db-gallery-thumb]', gallery).forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var src = thumb.dataset.dbSrc;
          var srcset = thumb.dataset.dbSrcset;
          var alt = thumb.dataset.dbAlt || '';
          if (src) {
            main.src = src;
            main.alt = alt;
            if (srcset) main.srcset = srcset;
          }
          $$('[data-db-gallery-thumb]', gallery).forEach(function (t) { t.setAttribute('aria-current', 'false'); });
          thumb.setAttribute('aria-current', 'true');
        });
      });
    });
  }

  /* --------------------------------------------------- reveal on scroll */
  function initReveal() {
    var targets = $$('.db-reveal');
    if (!targets.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (el) { el.dataset.visible = 'true'; });
      return;
    }
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.dataset.visible = 'true';
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });
    targets.forEach(function (el) { observer.observe(el); });
  }

  /* ------------------------------------------------- embed facades ----- */
  /* A third-party player is only fetched once the visitor asks for it.
     Without JS the streaming links beside it still work. */
  function initEmbeds() {
    $$('[data-db-embed]').forEach(function (wrapper) {
      var button = $('[data-db-embed-load]', wrapper);
      var template = $('[data-db-embed-markup]', wrapper);
      if (!button || !template) return;
      button.addEventListener('click', function () {
        wrapper.innerHTML = template.innerHTML;
        var frame = $('iframe', wrapper);
        if (frame) {
          frame.setAttribute('loading', 'lazy');
          frame.setAttribute('title', button.textContent.trim());
          frame.focus();
        }
      });
    });
  }

  /* ----------------------------------------------- free shipping bar --- */
  function initShipProgress() {
    $$('[data-db-ship-progress]').forEach(function (node) {
      var threshold = Number(node.dataset.dbThreshold || 0);
      var total = Number(node.dataset.dbTotal || 0);
      if (!threshold) return;
      var fill = $('[data-db-ship-fill]', node);
      if (fill) fill.style.width = Math.min(100, (total / threshold) * 100) + '%';
    });
  }

  /* --------------------------------------------------------- startup --- */
  function init() {
    initMenu();
    CartDrawer.init();
    initProductForms();
    initVariantPickers();
    initQuantity();
    initGallery();
    initReveal();
    initEmbeds();
    initShipProgress();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify's theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', init);

  window.DBCartDrawer = CartDrawer;
})();
